import { AuthContextState, AuthContextType } from "./AuthContext";
import { Endpoints, isDevelopment, User } from "consumerhouse-shared";
import { ErrorType } from "typed-react-form";

export class ValidationError extends Error {
    constructor(message?: string, data?: any) {
        super(message);
        this.name = "ValidationError";
        Error.captureStackTrace(this);
    }
}

export class RateLimitError extends Error {
    constructor(message?: string) {
        super(message);
        this.name = "RateLimitError";
        Error.captureStackTrace(this);
    }
}

export class NotFoundError extends Error {
    constructor(message?: string) {
        super(message);
        this.name = "NotFoundError";
        Error.captureStackTrace(this);
    }
}

export class ConsumerHouseClient {
    constructor(auth: AuthContextType | null) {
        this.auth = auth;
    }

    public auth: AuthContextType | null;
    public onAuth?: (auth: AuthContextType | null) => void;

    public async fetch<P extends keyof Endpoints>(
        url: P,
        body?: Endpoints[P][0],
        method = "POST",
        authTokenOverride?: string
    ): Promise<Endpoints[P][1]> {
        let accessToken = this.auth?.accessToken;
        if (authTokenOverride) {
            accessToken = authTokenOverride;
        }

        let res = await fetch(apiPath(url) as string, {
            method,
            body: typeof body === "object" ? JSON.stringify(body) : null,
            headers: {
                "Content-Type": "application/json",
                ...(accessToken && {
                    Authorization: `Bearer ${accessToken}`,
                }),
            },
        });

        if (res.status <= 400) {
            let text = (await res.text()).trim();
            if (!text) return undefined as any;
            return JSON.parse(text);
        } else if (res.status === 406) {
            throw new ValidationError("Validation error", await res.json());
        } else if (res.status === 429) {
            throw new RateLimitError("Rate limited");
        } else if (res.status === 404) {
            throw new NotFoundError("Not found: " + url);
        } else if (res.status === 403) {
            this.onAuth?.(null);
            return undefined as any;
        } else if (res.status === 401 && !authTokenOverride) {
            if (!this.auth) return undefined as any;
            try {
                this.auth = await this.fetch("/user/token", { refreshToken: this.auth?.refreshToken });
                this.onAuth?.(this.auth);
                return await this.fetch(url, body);
            } catch (ex) {
                this.onAuth?.(null);
                throw new Error("Could not refresh token: " + (ex as Error).message);
            }
        } else {
            throw new Error(`Could not ${method} '${url}' (HTTP ${res.status}: ${res.statusText})`);
        }
    }
}

export function apiPath(path: string): string {
    if (isDevelopment) return `http://localhost:3001/api${path}`;
    else return `/api${path}`;
}
